<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3 pb-4">
      <v-breadcrumbs :items="breadcrumbs" class="px-4 pr-5 mb-3 pt-4 pb-2">
        <template v-slot:divider>
          <v-icon>chevron_right</v-icon>
        </template>
        <template v-slot:item="{ item }">
          <router-link
            v-if="item.to"
            :to="item.to"
            class="text-decoration-none primary--text body-1"
          >{{ item.text }}</router-link>
          <span v-else class="title-style">{{ item.text }}</span>
        </template>
      </v-breadcrumbs>
      <template v-if="invoice">
        <v-row class="px-4">
          <v-col cols="12" class="d-flex align-center">
            <v-btn v-if="canPost" color="primary" outlined @click="post">
              <v-icon left>cloud_upload</v-icon>Post to QuickBooks
            </v-btn>
            <v-btn v-if="canRepost" color="primary" outlined @click="repost">
              <v-icon left>sync</v-icon>Re-post to QuickBooks
            </v-btn>
            <v-btn
              v-if="invoice.quickBooksInvoiceUrl"
              class="ml-2"
              color="primary"
              text
              :href="invoice.quickBooksInvoiceUrl"
              target="_blank"
              rel="noopener"
            >
              <v-icon left>open_in_new</v-icon>Open in QuickBooks
            </v-btn>
          </v-col>
        </v-row>
        <v-row class="px-4">
          <v-col cols="12" sm="3">
            <div class="caption grey--text">Client</div>
            <div class="body-2">{{ invoice.clientName }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text">Kind</div>
            <div class="body-2">{{ invoice.kindName }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text">Worker</div>
            <div class="body-2">{{ invoice.workerName }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text">State</div>
            <v-chip
              small
              label
              :color="postingStateAttrs(invoice.postingStateId).color"
              :dark="postingStateAttrs(invoice.postingStateId).dark"
            >{{ invoice.postingStateName }}</v-chip>
          </v-col>
        </v-row>
        <v-row class="px-4 mt-0">
          <v-col cols="12" sm="3">
            <div class="caption grey--text">Week Ending</div>
            <div class="body-2">{{ invoice.weekEnding ? $options.filters.formatDate(invoice.weekEnding) : '' }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text">Submitted</div>
            <div class="body-2">{{ invoice.vmsSubmitDate | formatDate }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text">Hours</div>
            <div class="body-2">{{ hoursLabel }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text">Amount</div>
            <div class="body-2">{{ invoice.totalAmount | currency }}</div>
          </v-col>
        </v-row>
        <v-row class="px-4 mt-0">
          <v-col cols="12" sm="3">
            <div class="caption grey--text">VMS Status</div>
            <div class="body-2">{{ invoice.vmsStatus }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text">Paid</div>
            <div class="body-2">{{ invoice.vmsPaidDate ? $options.filters.formatDate(invoice.vmsPaidDate) : '' }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text">Check / Ref</div>
            <div class="body-2">{{ invoice.vmsCheckNumber }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text">Paid Amount</div>
            <div class="body-2">{{ invoice.vmsPaidAmount != null ? $options.filters.currency(invoice.vmsPaidAmount) : '' }}</div>
          </v-col>
        </v-row>
        <v-row v-if="invoice.lastFailureMessage" class="px-4 mt-0">
          <v-col cols="12">
            <div class="caption grey--text">Failure</div>
            <div class="body-2 red--text">
              {{ failureReason }}
            </div>
          </v-col>
        </v-row>
      </template>
    </div>

    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <div class="subtitle-1 mb-2">Lines</div>
        <v-data-table
          dense
          :headers="lineHeaders"
          :items="lines"
          item-key="id"
          hide-default-footer
          :items-per-page="-1"
          class="elevation-2"
        >
          <template v-slot:item.hours="{ item }">
            {{ item.hours != null ? item.hours : '' }}
          </template>
          <template v-slot:item.amount="{ item }">
            {{ item.amount | currency }}
          </template>
        </v-data-table>

        <div class="subtitle-1 mt-6 mb-2">Posting History</div>
        <v-data-table
          dense
          :headers="attemptHeaders"
          :items="postingAttempts"
          item-key="id"
          show-expand
          hide-default-footer
          :items-per-page="-1"
          no-data-text="Not posted yet"
          class="elevation-2"
        >
          <template v-slot:item.startedAt="{ item }">
            {{ item.startedAt | formatDate }} {{ item.startedAt | toTimeOnlyString }}
          </template>
          <template v-slot:item.trigger="{ item }">
            {{ triggerLabel(item) }}
          </template>
          <template v-slot:item.outcomeStateName="{ item }">
            <v-chip
              v-if="item.outcomeStateId"
              x-small
              label
              :color="postingStateAttrs(item.outcomeStateId).color"
              :dark="postingStateAttrs(item.outcomeStateId).dark"
            >{{ item.outcomeStateName }}</v-chip>
          </template>
          <template v-slot:item.error="{ item }">
            <span class="red--text">{{ item.error }}</span>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="py-3">
              <div class="caption grey--text">Request Id</div>
              <div class="body-2 mb-2">{{ item.requestId }}</div>
              <div class="caption grey--text">Request</div>
              <pre class="attempt-body mb-2">{{ item.requestBody }}</pre>
              <div class="caption grey--text">Response</div>
              <pre class="attempt-body">{{ item.responseBody }}</pre>
            </td>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { BottomSpacer } from '@/components/buttons'
import { invoicesRoute } from '@/lib/constants/routes'
import { invoicePostingStateAttrs } from '@/lib/constants/invoices'
import { failureReason } from '@/lib/invoices'
import { attemptTriggerLabel, canPostInvoice, canRepostInvoice, totalHoursLabel } from './invoiceHelpers'

export default {
  components: {
    BottomSpacer
  },
  data () {
    return {
      lineHeaders: [
        { text: 'Source Document Id', value: 'sourceDocumentId', sortable: false, width: '50%' },
        { text: 'Hours', value: 'hours', align: 'center', sortable: false, width: '20%' },
        { text: 'Amount', value: 'amount', align: 'end', sortable: false, width: '30%' }
      ],
      attemptHeaders: [
        { text: 'Started', value: 'startedAt', sortable: false, width: '15%' },
        { text: 'Triggered By', value: 'trigger', sortable: false, width: '15%' },
        { text: 'Outcome', value: 'outcomeStateName', align: 'center', sortable: false, width: '12%' },
        { text: 'Category', value: 'failureCategory', sortable: false, width: '13%' },
        { text: 'Error', value: 'error', sortable: false, width: '42%' },
        { text: '', value: 'data-table-expand', width: '3%' }
      ]
    }
  },
  computed: {
    ...mapFields('invoice', ['invoice', 'lines', 'postingAttempts']),
    canPost () {
      return canPostInvoice(this.invoice)
    },
    canRepost () {
      return canRepostInvoice(this.invoice)
    },
    failureReason () {
      return failureReason(this.invoice)
    },
    hoursLabel () {
      return totalHoursLabel(this.invoice)
    },
    breadcrumbs () {
      return [
        { text: 'Invoices', to: { name: invoicesRoute.name } },
        { text: this.invoice ? this.invoice.vmsInvoiceId : 'Invoice', disabled: true }
      ]
    }
  },
  methods: {
    ...mapActions('invoice', ['getInvoice', 'postInvoice', 'repostInvoice', 'clearInvoice']),
    post () {
      const id = this.invoice.id
      return this.postInvoice({ id }).then(() => this.getInvoice({ id }))
    },
    repost () {
      const id = this.invoice.id
      return this.repostInvoice({ id }).then(() => this.getInvoice({ id }))
    },
    postingStateAttrs (stateId) {
      return invoicePostingStateAttrs(stateId)
    },
    triggerLabel (attempt) {
      return attemptTriggerLabel(attempt)
    }
  },
  created () {
    return this.getInvoice({ id: parseInt(this.$route.params.id) })
  },
  beforeDestroy () {
    this.clearInvoice()
  }
}
</script>

<style scoped>
.attempt-body {
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 12px;
}
</style>
